import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@loot-os/ui/components/alert-dialog";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import { Card, CardContent } from "@loot-os/ui/components/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@loot-os/ui/components/dialog";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FlaskConical, Globe, Pencil, Plus } from "lucide-react";
import { useState } from "react";
import { setActiveEnvironment, useActiveEnvironmentId } from "../lib/environment";
import { formatDate } from "../lib/format";
import { useTRPC } from "../lib/trpc";

export function SandboxesPage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const activeId = useActiveEnvironmentId();
  const envs = useQuery(trpc.environments.list.queryOptions());

  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState("");
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const [confirmName, setConfirmName] = useState("");

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: trpc.environments.list.queryOptions().queryKey,
    });
  };

  const create = useMutation(
    trpc.environments.create.mutationOptions({
      onSuccess: () => {
        invalidate();
        setShowCreate(false);
        setCreateName("");
      },
    }),
  );
  const rename = useMutation(
    trpc.environments.rename.mutationOptions({
      onSuccess: () => {
        invalidate();
        setRenaming(null);
      },
    }),
  );
  const remove = useMutation(
    trpc.environments.delete.mutationOptions({
      onSuccess: (_data, variables) => {
        // If the deleted sandbox was active, fall back to Production — the
        // scope store's cache reset also refetches the environments list.
        if (activeId === variables.environmentId) {
          setActiveEnvironment(null);
        } else {
          invalidate();
        }
      },
    }),
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Sandboxes</h1>
          <p className="text-sm text-muted-foreground">
            Isolated test environments. Users, balances, opens, boxes and API keys in a sandbox
            never touch production.
          </p>
        </div>
        <Button type="button" onClick={() => setShowCreate(true)}>
          <Plus />
          Create sandbox
        </Button>
      </div>

      <Card>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {envs.data?.map((env) => (
                <TableRow key={env.id}>
                  <TableCell className="font-medium">
                    <span className="inline-flex items-center gap-2">
                      {env.type === "sandbox" ? (
                        <FlaskConical className="size-4 text-amber-500" />
                      ) : (
                        <Globe className="size-4" />
                      )}
                      {env.name}
                      {env.id === activeId && <Badge variant="secondary">active</Badge>}
                    </span>
                  </TableCell>
                  <TableCell>
                    <code className="font-mono text-xs text-muted-foreground">{env.slug}</code>
                  </TableCell>
                  <TableCell>
                    <Badge variant={env.type === "live" ? "default" : "outline"}>{env.type}</Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(env.createdAt)}
                  </TableCell>
                  <TableCell className="text-right">
                    {env.type === "sandbox" && (
                      <div className="inline-flex items-center gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            rename.reset();
                            setRenaming({ id: env.id, name: env.name });
                          }}
                        >
                          <Pencil />
                          Rename
                        </Button>
                        <AlertDialog onOpenChange={(open) => !open && setConfirmName("")}>
                          <AlertDialogTrigger
                            render={
                              <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                disabled={remove.isPending}
                              />
                            }
                          >
                            Delete
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete "{env.name}"?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This permanently deletes every user, open, ledger entry, box and API
                                key in this sandbox, along with its storefront and shoppers. Keys
                                minted here stop working immediately. Type the sandbox name to
                                confirm.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <Input
                              type="text"
                              value={confirmName}
                              onChange={(e) => setConfirmName(e.target.value)}
                              placeholder={env.name}
                            />
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                disabled={confirmName !== env.name}
                                onClick={() => remove.mutate({ environmentId: env.id })}
                              >
                                Delete sandbox
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showCreate} onOpenChange={(open) => !open && setShowCreate(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create sandbox</DialogTitle>
            <DialogDescription>
              A fresh, empty environment with its own storefront. Mint API keys in it from the API
              Keys page after switching to it.
            </DialogDescription>
          </DialogHeader>
          <form
            className="grid gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate({ name: createName });
            }}
          >
            <div className="grid gap-2">
              <Label htmlFor="sandbox-name">Name</Label>
              <Input
                id="sandbox-name"
                type="text"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="Staging"
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending || !createName.trim()}>
                {create.isPending ? "Creating…" : "Create sandbox"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={renaming !== null} onOpenChange={(open) => !open && setRenaming(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename sandbox</DialogTitle>
            <DialogDescription>
              Renames the display name only — the sandbox's slug and storefront URL stay the same.
            </DialogDescription>
          </DialogHeader>
          <form
            className="grid gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (renaming) rename.mutate({ environmentId: renaming.id, name: renaming.name });
            }}
          >
            <div className="grid gap-2">
              <Label htmlFor="sandbox-rename">Name</Label>
              <Input
                id="sandbox-rename"
                type="text"
                value={renaming?.name ?? ""}
                onChange={(e) =>
                  setRenaming((prev) => (prev ? { ...prev, name: e.target.value } : prev))
                }
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRenaming(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={rename.isPending || !renaming?.name.trim()}>
                {rename.isPending ? "Saving…" : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
